import { useState, useEffect, useRef } from "react";
import axiosClient from "../../api/axiosClient";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingSpinner from "../../components/LoadingSpinner";
import "./Auth.css";

function Signup() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 = form, 2 = OTP
  const [loading, setLoading] = useState(false);

  // 🟢 TIMER STATES
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const intervalRef = useRef(null); // 🔥 IMPORTANT

  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  });

  const [otp, setOtp] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* =====================================================
     START / RESTART RESEND TIMER
  ===================================================== */
  const startResendTimer = () => {
    // Clear old timer if exists
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setTimer(60);
    setCanResend(false);

    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  /* =====================================================
     START TIMER WHEN OTP SCREEN OPENS
  ===================================================== */
  useEffect(() => {
    if (step === 2) {
      startResendTimer();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [step]);

  /* =====================================================
     STEP 1 — SEND OTP (EMAIL)
  ===================================================== */
  const sendOTP = async (e) => {
    e.preventDefault();

    if (form.password !== form.confirmPassword) {
      toast.warning("Passwords do not match");
      return;
    }

    try {
      setLoading(true);

      await axiosClient.post("/auth/send-otp", form);

      toast.success("OTP sent to your email");
      setStep(2);

    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     STEP 2 — VERIFY OTP (EMAIL)
  ===================================================== */
  const verifyOTP = async () => {
    try {
      setLoading(true);

      await axiosClient.post("/auth/verify-otp", {
        email: form.email,
        otp,
      });

      toast.success("Account created successfully!");
      navigate("/login");

    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     STEP 3 — RESEND OTP (FIXED)
  ===================================================== */
  const resendOTP = async () => {
    try {
      await axiosClient.post("/auth/resend-otp", {
        email: form.email,
      });

      toast.success("OTP resent to email");

      // 🔥 RESTART TIMER PROPERLY
      startResendTimer();

    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    }
  };

  const loadingMessage = step === 1 ? "Sending OTP" : "Verifying OTP";
  const loadingSub    = step === 1 ? "We're sending a code to your email…" : "Checking your code…";

  return (
    <div className="auth-page">
      {loading && (
        <LoadingSpinner overlay message={loadingMessage} sub={loadingSub} />
      )}
      <div className="auth-container">
        <div className="auth-header">
          <h2>Create <span>Account</span></h2>
        </div>

        {/* ================= STEP 1 ================= */}
        {step === 1 && (
          <form onSubmit={sendOTP} className="auth-form">
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              className="auth-input"
              value={form.name}
              onChange={handleChange}
              required
            />

            <input
              type="email"
              name="email"
              placeholder="Email Address"
              className="auth-input"
              value={form.email}
              onChange={handleChange}
              required
            />

            <input
              type="tel"
              name="mobile"
              placeholder="Mobile Number (+91XXXXXXXXXX)"
              className="auth-input"
              value={form.mobile}
              onChange={handleChange}
              required
            />

            <input
              type="password"
              name="password"
              placeholder="Password"
              className="auth-input"
              value={form.password}
              onChange={handleChange}
              required
            />

            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirm Password"
              className="auth-input"
              value={form.confirmPassword}
              onChange={handleChange}
              required
            />

            <button className="auth-submit" disabled={loading}>
              Send OTP
            </button>

            <p className="auth-back" onClick={() => navigate("/login")}>
              ← Back to login
            </p>
          </form>
        )}

        {/* ================= STEP 2 — OTP SCREEN ================= */}
        {step === 2 && (
          <div className="auth-form">
            <p className="auth-otp-info">
              OTP sent to <b>{form.email}</b>
            </p>

            <input
              type="text"
              placeholder="Enter OTP"
              className="auth-input"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
            />

            <button className="auth-submit" onClick={verifyOTP} disabled={loading}>
              Verify OTP
            </button>

            <div className="auth-resend-container">
              {canResend ? (
                <button className="auth-resend-btn" onClick={resendOTP}>
                  Resend OTP
                </button>
              ) : (
                <p style={{ margin: 0 }}>Resend OTP in {timer}s</p>
              )}
            </div>

            <p
              className="auth-back"
              onClick={() => setStep(1)}
            >
              ← Change email
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Signup;
